import React, { forwardRef } from 'react';
import { cn } from '../lib/cn';
import { Button } from './Button';
import { X } from 'lucide-react';

export interface BulkActionBarProps extends React.HTMLAttributes<HTMLDivElement> {
  selectedCount: number;
  onClearSelection: () => void;
  children: React.ReactNode;
}

export const BulkActionBar = forwardRef<HTMLDivElement, BulkActionBarProps>(
  ({ selectedCount, onClearSelection, children, className, ...props }, ref) => {
    const isVisible = selectedCount > 0;

    return (
      <div 
        ref={ref}
        className={cn(
          "fixed bottom-6 left-1/2 -translate-x-1/2 z-40 transition-all duration-300 ease-in-out",
          isVisible 
            ? "translate-y-0 opacity-100 pointer-events-auto" 
            : "translate-y-16 opacity-0 pointer-events-none",
          className
        )}
        aria-live="polite"
        {...props}
      >
        <div className="flex items-center gap-4 px-4 py-3 bg-background/80 backdrop-blur-md border border-border shadow-lg rounded-2xl">
          <div className="flex items-center gap-2 pr-4 border-r border-border">
            <span className="text-sm font-medium bg-primary/10 text-primary px-2.5 py-1 rounded-full">
              {selectedCount} selected
            </span>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7 rounded-full text-muted-foreground hover:text-foreground"
              onClick={onClearSelection}
              aria-label="Clear selection"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {children}
          </div>
        </div>
      </div>
    );
  }
);

BulkActionBar.displayName = 'BulkActionBar';
